import * as React from 'react';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import List from '@material-ui/core/List';
import UpdateIcon from '@material-ui/icons/Update';
import { DeepReadonly } from 'utility-types';
import { IconListItem } from '@pec/aion-ui-components/components/IconListItem';
import { ISubscription, SubscriptionStatus } from '@pec/aion-ui-core/interfaces/subscription';
import { IVerification } from 'interfaces/verification';
import { Link } from '@material-ui/core';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { OshaVerificationStatus } from '@pec/aion-ui-core/slices/oshaViolations';
import { RouteComponentProps, withRouter } from 'react-router';
import { Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Tile } from 'components/Tile';
import { Trans, useTranslation } from 'react-i18next';

type RouteParams = {
  organizationId?: string;
};

type OwnProps = {
  subscriptions: DeepReadonly<ISubscription[]>;
  verifications: DeepReadonly<IVerification[]>;
  oshaViolationsStatus: OshaVerificationStatus | null;
  hasOSHAViolationsIntegrationFeature?: boolean;
};

const styles = (theme: Theme) => ({
  good: {
    backgroundColor: theme.palette.secondary.main
  },
  bad: {
    backgroundColor: theme.palette.error.main
  },
  pending: {
    backgroundColor: theme.palette.grey[600]
  },
  redLink: {
    color: theme.palette.error.main
  }
});

type Props = WithStyles<typeof styles> & RouteComponentProps<RouteParams> & OwnProps;

const VerificationsTile: React.FC<Props> = ({
  match: {
    params: { organizationId }
  },
  subscriptions,
  verifications,
  oshaViolationsStatus,
  hasOSHAViolationsIntegrationFeature,
  classes
}) => {
  const { t } = useTranslation();

  // exclude OSHA citation, now fetched from a separate endpoint
  const filteredVerifications = verifications.filter(v =>
    hasOSHAViolationsIntegrationFeature ? v.name !== 'OSHA Citation' : true
  );

  const oshaViolationIconMeta: Record<
    OshaVerificationStatus,
    { classKey: keyof typeof classes; Icon: React.ElementType; title: string; primaryText: React.ReactNode }
  > = {
    [OshaVerificationStatus.NoViolations]: {
      classKey: 'good',
      Icon: DoneIcon,
      title: t('subscriber:dashboard.verificationsTile.osha.noViolations.title', 'No OSHA violations found'),
      primaryText: (
        <Trans
          i18nKey="subscriber:dashboard.verificationsTile.osha.noViolations.text"
          defaultValue="OSHA Violations: <1>No violations</1>"
          components={{
            1: <Link href={`/organizations/${organizationId}/osha-violations`} underline="always" />
          }}
        ></Trans>
      )
    },
    [OshaVerificationStatus.Pending]: {
      classKey: 'pending',
      Icon: UpdateIcon,
      title: t(
        'subscriber:dashboard.verificationsTile.osha.pending.title',
        'No verified data found (pending verification)'
      ),
      primaryText: (
        <Trans
          i18nKey="subscriber:dashboard.verificationsTile.osha.pending.text"
          defaultValue="OSHA Violations: <1>Pending Verification</1>"
          components={{
            1: <Link href={`/organizations/${organizationId}/osha-violations`} underline="always" />
          }}
        ></Trans>
      )
    },
    [OshaVerificationStatus.ViolationsFound]: {
      classKey: 'bad',
      Icon: CloseIcon,
      title: t('subscriber:dashboard.verificationsTile.osha.violations.title', 'OSHA violations found'),
      primaryText: (
        <Trans
          i18nKey="subscriber:dashboard.verificationsTile.osha.violations.text"
          defaultValue="OSHA Violations: <1>Violations found</1>"
          components={{
            1: (
              <Link
                href={`/organizations/${organizationId}/osha-violations`}
                className={classes.redLink}
                underline="always"
              />
            )
          }}
        ></Trans>
      )
    }
  };

  const oshaViolationIcon =
    hasOSHAViolationsIntegrationFeature && oshaViolationsStatus !== null && oshaViolationIconMeta[oshaViolationsStatus];

  return (
    <Tile primaryText={t('subscriber:dashboard.verificationsTile.title', 'Subscription & Verifications')}>
      <List>
        {subscriptions.length + filteredVerifications.length > 0 &&
          subscriptions.map(({ id, isExpired, expirationDate, status, typeDisplayName }) =>
            isExpired ? (
              <IconListItem
                key={id}
                className={classes.bad}
                icon={<CloseIcon />}
                title={status}
                primaryText={t('subscriber:dashboard.verificationsTile.expiredDate', {
                  typeDisplayName,
                  date: localizeDate(expirationDate, t),
                  defaultValue: '{{typeDisplayName}} expired on {{date}}'
                })}
                useMuiTooltip
                muiTooltipProps={{ arrow: true }}
              />
            ) : status === SubscriptionStatus.Inactive ? (
              <IconListItem
                key={id}
                className={classes.bad}
                icon={<CloseIcon />}
                title={status}
                primaryText={t('subscriber:dashboard.verificationsTile.expire', {
                  typeDisplayName,
                  defaultValue: '{{typeDisplayName}} expired'
                })}
                useMuiTooltip
                muiTooltipProps={{ arrow: true }}
              />
            ) : expirationDate === null || expirationDate === '' ? (
              <IconListItem
                key={id}
                className={classes.good}
                icon={<DoneIcon />}
                title={status}
                primaryText={typeDisplayName}
                useMuiTooltip
                muiTooltipProps={{ arrow: true }}
              />
            ) : (
              <IconListItem
                key={id}
                className={classes.good}
                icon={<DoneIcon />}
                title={status}
                primaryText={t('subscriber:dashboard.verificationsTile.expiresOn', {
                  typeDisplayName,
                  date: localizeDate(expirationDate, t),
                  defaultValue: '{{typeDisplayName}} expires on {{date}}'
                })}
                useMuiTooltip
                muiTooltipProps={{ arrow: true }}
              />
            )
          )}
        {filteredVerifications.map(({ legacyId, name, status, isVerified }) =>
          isVerified ? (
            <IconListItem
              key={legacyId}
              className={classes.good}
              icon={<DoneIcon />}
              title={status}
              primaryText={name}
              useMuiTooltip
              muiTooltipProps={{ arrow: true }}
            />
          ) : isVerified === false ? (
            <IconListItem
              key={legacyId}
              className={classes.bad}
              icon={<CloseIcon />}
              title={status}
              primaryText={name}
              useMuiTooltip
              muiTooltipProps={{ arrow: true }}
            />
          ) : (
            <IconListItem
              key={legacyId}
              icon={<UpdateIcon />}
              title={status}
              primaryText={name}
              useMuiTooltip
              muiTooltipProps={{ arrow: true }}
            />
          )
        )}
        {filteredVerifications.length === 0 && (
          <IconListItem
            to={`/${organizationId}/questionnaire`}
            icon={<ArrowForwardIosIcon />}
            primaryText={t('subscriber:dashboard.verificationsTile.noVerifications', 'No Verifications are Available')}
            useMuiTooltip
            muiTooltipProps={{ arrow: true }}
          >
            {t(
              'subscriber:dashboard.verificationsTile.completeSections',
              'Please complete the appropriate sections of your questionnaire.'
            )}
          </IconListItem>
        )}
        {oshaViolationIcon && (
          <IconListItem
            className={classes[oshaViolationIcon.classKey]}
            icon={<oshaViolationIcon.Icon />}
            title={oshaViolationIcon.title}
            primaryText={oshaViolationIcon.primaryText}
            useMuiTooltip
            muiTooltipProps={{ arrow: true }}
          />
        )}
      </List>
    </Tile>
  );
};

export const VerificationsTileComponent = withStyles(styles)(withRouter(VerificationsTile));
